import {Controller} from '@hotwired/stimulus';

export default class JsonOptionsController extends Controller {
  static targets = [
    'dragContainer',
    'jsonEnabled',
    'jsonOptions',
    'jsonOptionRow'
  ];

  declare readonly dragContainerTarget: HTMLElement;
  declare readonly hasDragContainerTarget: boolean;

  declare readonly jsonEnabledTarget: HTMLInputElement;
  declare readonly jsonOptionsTarget: HTMLInputElement;
  declare readonly jsonOptionRowTargets: HTMLTableRowElement[];

  connect() {
    if (this.hasDragContainerTarget) {
      this.setupDragAndDrop();
    }

    this.toggleOptions();
  }

  moveUpRow(event: { target: HTMLElement }) {
    const row = event.target.closest('tr') as HTMLTableRowElement;
    const idx = this.jsonOptionRowTargets.indexOf(row);
    if (idx > 0) {
      this.jsonOptionRowTargets[idx - 1].before(row);
    }

    return false;
  }

  moveDownRow(event: { target: HTMLElement }) {
    const row = event.target.closest('tr') as HTMLTableRowElement;
    const idx = this.jsonOptionRowTargets.indexOf(row);
    if (idx < this.jsonOptionRowTargets.length - 1) {
      this.jsonOptionRowTargets[idx + 1].after(row);
    }

    return false;
  }

  moveRowToTheTop(event: { target: HTMLElement }) {
    const row = event.target.closest('tr') as HTMLTableRowElement;
    const first = this.jsonOptionRowTargets[0];

    if (first && first !== row) {
      first.before(row);
    }

    return false;
  }

  moveRowToTheBottom(event: { target: HTMLElement }) {
    const row = event.target.closest('tr') as HTMLTableRowElement;
    const last = this.jsonOptionRowTargets[this.jsonOptionRowTargets.length - 1];

    if (last && last !== row) {
      last.after(row);
    }

    return false;
  }

  removeOption(event: MouseEvent) {
    const self = event.target as HTMLAnchorElement;
    const row = self.closest('tr');

    if (row && this.jsonOptionRowTargets.length > 1) {
      row.remove();
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    return true; // send off deletion
  }

  addOption() {
    const count = this.jsonOptionRowTargets.length;
    const last = this.jsonOptionRowTargets[count - 1];
    const dup = last.cloneNode(true) as HTMLElement;

    const inputs = dup.querySelectorAll('input');

    inputs.forEach((el) => {
      let name = el.getAttribute('name');
      if (name) {
        name = name.replace(/\[\d+]/, `[${count}]`);
        el.setAttribute('name', name);
      }
      let id = el.getAttribute('id');
      if (id) {
        id = id.replace(/_\d+_/, `_${count}_`);
        el.setAttribute('id', id);
      }
      if (el.type === 'text') el.value = '';
      if (el.type === 'checkbox') el.checked = false;
    });

    last.insertAdjacentElement('afterend', dup);

    return false;
  }

  private setupDragAndDrop() {
    // Make custom fields draggable
    // eslint-disable-next-line no-undef
    const drake = dragula([this.dragContainerTarget], {
      isContainer: () => false,
      moves: (el, source, handle: HTMLElement) => handle.classList.contains('dragula-handle'),
      accepts: () => true,
      invalid: () => false,
      direction: 'vertical',
      copy: false,
      copySortSource: false,
      revertOnSpill: true,
      removeOnSpill: false,
      mirrorContainer: this.dragContainerTarget,
      ignoreInputTextSelection: true,
    });

    // Setup autoscroll
    void window.OpenProject.getPluginContext().then((pluginContext) => {
      // eslint-disable-next-line no-new
      new pluginContext.classes.DomAutoscrollService(
        [
          document.getElementById('content-wrapper') as HTMLElement,
        ],
        {
          margin: 25,
          maxSpeed: 10,
          scrollWhenOutside: true,
          autoScroll: () => drake.dragging,
        },
      );
    });
  }

  toggleOptions() {
    let enabled = this.jsonEnabledTarget.checked;
    this.jsonOptionsTarget.hidden = !enabled;
  }
}